import {
  ADMIN_CONCIERGE,
  ADMIN_DASHBOARD,
  DELIVERIES_HOME,
  ADMIN_ROOT,
  ContentStyled,
  handlePartnerAuthNavigation,
  isAdmin,
  isDeliveries,
  isPartner,
  isPushApp,
  isSales,
  useAuth,
  useConversationsSocket,
  User,
  UserRole,
  useSidebarState,
} from '@vgrubs/components';
import { useFirebaseNotification } from 'components/src/hooks/useFirebaseNotification';
import { useThemeColor } from 'components/src/hooks/useThemeColor';
import { useUnreadTabTitle } from 'components/src/hooks/useUnreadTabTitle';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { ActiveAnnouncements } from '../../../adminPages/Announcements/ActiveAnnouncements';
import { ChatBubble } from '../../../adminPages/Chat/ChatBubble';
import { BellScreensProvider } from '../../../adminPages/Notifications/bellScreens';
import { useNotificationsSocket } from '../../../adminPages/Notifications/hooks/useNotificationsSocket';
import { AdminSidebar } from './AdminSidebar';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from './layoutMetrics';

const AdminLayoutStyled = styled.div`
  display: flex;
`;

/**
 * "Skip to main content" link.
 *
 * Visually hidden until focused — appears at the top-left of the viewport
 * when the user tabs into it (typically the first Tab from page load).
 * Lets keyboard / screen-reader users bypass the sidebar's many nav
 * items and jump straight to the page content. WCAG 2.4.1 Bypass Blocks.
 */
const SkipLink = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  padding: 12px 20px;
  border-radius: 0 0 8px 0;
  font-weight: 500;
  z-index: 99999;
  transform: translateY(-150%);
  transition: transform 0.15s ease;

  &:focus {
    transform: translateY(0);
    outline: 2px solid ${({ theme }) => theme.white};
    outline-offset: 2px;
  }
`;

const ContentWrapperStyled = styled.div<{ $sidebarIsOpen: boolean }>`
  width: ${({ $sidebarIsOpen }) =>
    $sidebarIsOpen
      ? `calc(100% - ${SIDEBAR_WIDTH})`
      : `calc(100% - ${SIDEBAR_WIDTH_COLLAPSED})`};
  margin-left: ${({ $sidebarIsOpen }) =>
    $sidebarIsOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED};
  transition: ${({ theme }) => theme.transitions.ease200ms};
  @media (max-width: 800px) {
    margin-left: 0;
    width: 100%;
  }
`;

export const AdminLayout = ({
  children,
  routes,
  sidebarTop,
}: {
  children: React.ReactNode;
  routes: any;
  sidebarTop?: React.ReactNode;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useAuth((state) => state);
  const { name, email, id } = userData.user as User;

  if (isPushApp) {
    useFirebaseNotification();
  }

  /* Unread messages in the tab label, so a background tab still says so. */
  useUnreadTabTitle();

  /*
   * Deliberately NOT wrapped in a conditional like the line above: that one
   * only survives because `isPushApp` is a module constant.
   *
   * Gated on role rather than on `conversationRead`: an internal user with no
   * permission still has their own groups and direct messages, and those need
   * the socket as much as a support thread does.
   */
  const { role } = (userData.user ?? {}) as User;
  useConversationsSocket({
    enabled: isAdmin
      ? role === UserRole.admin ||
        role === UserRole.support ||
        role === UserRole.marketing
      : isPartner &&
        (role === UserRole.partner ||
          role === UserRole.manager ||
          role === UserRole.staff),
  });

  /* The bell's socket. Once here rather than in the bell, which lives in each
     page's header and would reconnect on every navigation. Decides for itself
     who gets one. */
  useNotificationsSocket();

  useEffect(() => {
    if ((window as any).Atlas) {
      (window as any).Atlas?.call('identify', {
        userId: id,
        name,
        email,
      });
    }
  }, []);

  useEffect(() => {
    if (location.pathname === ADMIN_ROOT && isAdmin) navigate(ADMIN_DASHBOARD);
    if (location.pathname === ADMIN_ROOT && isPartner) {
      handlePartnerAuthNavigation(userData, false);
    }
    if (location.pathname === ADMIN_ROOT && isSales) navigate(ADMIN_CONCIERGE);
    // ADMIN_ROOT is '' while the root's pathname is '/', so the checks above
    // never match there; the console's `/` (also the PWA start_url) does.
    if (location.pathname === '/' && isDeliveries)
      navigate(DELIVERIES_HOME, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const {
    sidebarIsOpen: sidebarIsOpenState,
    setSidebarIsOpen: setSidebarIsOpenState,
  } = useSidebarState((state) => state);
  const sidebarIsOpen = sidebarIsOpenState;

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 800
  );

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = windowWidth <= 800;

  // Through ThemeProvider so a navy-primary app paints its own chrome.
  const theme = useTheme();

  /*
   * On a phone the open sidebar is the whole screen, primary to the edges, so
   * the status bar joins it - and goes back to the page's cream when it
   * closes. Only on a phone: on a desktop the sidebar is a column beside a
   * cream page, and the browser chrome belongs to the page.
   */
  useThemeColor(isMobile && sidebarIsOpen ? theme.primary : theme.background);

  const prevIsMobileRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevIsMobileRef.current === null) {
      if (isMobile) setSidebarIsOpenState(false);
    } else if (!prevIsMobileRef.current && isMobile) {
      setSidebarIsOpenState(false);
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile, setSidebarIsOpenState]);

  return (
    <AdminLayoutStyled>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <AdminSidebar routes={routes} sidebarTop={sidebarTop} />
      <ContentWrapperStyled $sidebarIsOpen={sidebarIsOpen}>
        <ContentStyled as="main" id="main-content" tabIndex={-1}>
          {/* The headers inside read from it which screens get the bell. */}
          <BellScreensProvider routes={routes}>{children}</BellScreensProvider>
        </ContentStyled>
      </ContentWrapperStyled>
      {/* A module constant, same as the notification mount above - the other
          builds never ship the bubble at all. */}
      {isPartner && <ChatBubble />}
      {/* Announcements replaced the hardcoded chat What's New that used to
          mount here. Sales is left out on purpose - nobody writes
          announcements for it yet, and its layout is shared with screens that
          cannot afford an interruption. */}
      {(isAdmin || isPartner) && <ActiveAnnouncements />}
    </AdminLayoutStyled>
  );
};
